/**
 * FluorideOptions - Class-based implementation for handling fluoride product options
 * Handles variant selection, refill confirmation modal, and UI updates
 *
 * Key functionality:
 * - Intercepts radio button changes for variant selection
 * - Shows a confirmation modal when "Refill Pack" is selected
 * - Handles confirmation flow and prevents URL updates until confirmed
 * - Updates UI based on variant selection
 */
class FluorideOptions {
  constructor() {
    // State variables
    this.previousVariant = null; // Stores the previously selected variant object
    this.pendingRefillConfirmation = false; // Tracks if we're waiting for refill confirmation
    this.lastSelectedVariant = null; // Stores the last selected variant object
    this.pendingRadioTarget = null; // Stores the radio button that triggered the confirmation
    this.previousSelectedRadio = null; // Stores the previously selected radio button (to revert if needed)
    this.processingConfirmation = false; // Flag to prevent event loops during confirmation handling

    // Initialize the modal using BorouxModal class
    this.refillModal = new BorouxModal({
      id: 'fluoride-filter-shells-confirm-modal',
      onOpen: () => {
        console.log('Refill confirmation modal opened');
      },
      onClose: () => {
        console.log('Refill confirmation modal closed');
        this.closeRefillConfirmModal();
      },
    });

    // Bind methods to maintain 'this' context
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleWindowClick = this.handleWindowClick.bind(this);
    this.updateVariantUI = this.updateVariantUI.bind(this);
    this.closeRefillConfirmModal = this.closeRefillConfirmModal.bind(this);
    this.confirmRefillSelection = this.confirmRefillSelection.bind(this);
    this.handleRadioChange = this.handleRadioChange.bind(this);

    // Initialize
    this.init();
  }

  /**
   * Initialize event listeners and store initial state
   */
  init() {
    // Listen for variant change events (for UI updates)
    document.addEventListener(
      'product:variant-change',
      this.handleVariantChange,
      true
    );

    // Add direct event listeners to variant inputs
    const optionIndex = document.getElementById('buttonOptionIndex')?.value;
    if (!optionIndex) {
      console.error('Button option index not found');
      return;
    }

    const optionHandle = document.querySelector(
      `[data-index="option${optionIndex}"]`
    );

    if (optionHandle) {
      const radios = optionHandle.querySelectorAll('input[type="radio"]');

      // Store the initially selected radio
      radios.forEach(radio => {
        if (radio.checked) {
          this.previousSelectedRadio = radio;
        }

        // Add change event listener to each radio button (capture phase to intercept early)
        radio.addEventListener('change', this.handleRadioChange, true);
      });
    }

    // Add event listeners to the confirmation buttons
    const yesButton = document.getElementById('fluoride-shell-yes');
    if (yesButton) {
      yesButton.addEventListener('click', () =>
        this.confirmRefillSelection(true)
      );
    }

    const noButton = document.getElementById('fluoride-shell-no');
    if (noButton) {
      noButton.addEventListener('click', () =>
        this.confirmRefillSelection(false)
      );
    }

    // Expose methods to global scope for HTML onclick attributes
    window.closeRefillConfirmModal = this.closeRefillConfirmModal;
    window.confirmRefillSelection = this.confirmRefillSelection;
  }

  /**
   * Handles radio button change events
   * Intercepts "Refill Pack" selections to show confirmation modal
   * @param {Event} event - The change event
   * @returns {boolean} - Whether to allow the event to continue propagating
   */
  handleRadioChange(event) {
    console.log('handleRadioChange called with event:', event);
    const radio = event.target;
    console.log('Radio value:', radio.value);
    console.log('Radio checked:', radio.checked);

    // Skip handling if we're currently processing a confirmation
    // This prevents infinite loops when programmatically triggering events
    if (this.processingConfirmation) {
      console.log(
        'Skipping radio change handling during confirmation processing'
      );
      return true;
    }

    // Skip our handling if this is a programmatic event (not trusted) and we're in confirmation mode
    if (!event.isTrusted && this.pendingRefillConfirmation) {
      console.log(
        'Allowing programmatic event to pass through during confirmation'
      );
      return true;
    }

    // Check if this is a Refill Pack variant
    if (radio.value.includes('Refill') && !this.pendingRefillConfirmation) {
      console.log('Refill Pack variant detected');

      // Prevent the default change behavior to stop URL/variant updates
      event.preventDefault();
      event.stopPropagation();

      // Store the target for later use - IMPORTANT: Store as a global variable to ensure it persists
      // This dual storage approach helps ensure the reference doesn't get lost between event handlers
      window.pendingRefillRadio = radio;
      this.pendingRadioTarget = radio;
      console.log('pendingRadioTarget set:', this.pendingRadioTarget);

      // Uncheck this radio button to prevent the variant from changing
      radio.checked = false;

      // If we have a previous radio, make sure it stays checked
      if (this.previousSelectedRadio) {
        console.log(
          'Keeping previousSelectedRadio checked:',
          this.previousSelectedRadio
        );
        this.previousSelectedRadio.checked = true;
      }

      // Show confirmation modal using BorouxModal
      this.refillModal.open();
      this.pendingRefillConfirmation = true;

      return false;
    }

    // Store this as the previous selected radio for future reference
    console.log('Setting previousSelectedRadio:', radio);
    this.previousSelectedRadio = radio;
  }

  /**
   * Handles variant change events
   * Used for UI updates after variant selection
   * @param {CustomEvent} evt - The variant change event
   */
  handleVariantChange(evt) {
    const variant = evt.detail.variant;

    // We're now handling the refill pack confirmation before this event is triggered
    // This handler is just for UI updates

    // Update the UI based on the variant
    this.updateVariantUI(variant);

    // Store the previous variant for reference
    this.previousVariant = variant;
  }

  /**
   * Handles clicks on the window
   * Used to close the modal when clicking outside
   * @param {Event} event - The click event
   */
  handleWindowClick(event) {
    // No longer needed as BorouxModal handles this
  }

  /**
   * Updates UI elements based on the selected variant
   * @param {Object} variant - The selected variant object
   */
  updateVariantUI(variant) {
    // Enable/Disable ATC button based on availability
    const addToCartButton = document.getElementById('AddToCart');
    const buttonText = document.getElementById('atc_button_text');

    if (addToCartButton && buttonText) {
      if (variant.available) {
        addToCartButton.disabled = false;
        buttonText.innerHTML = 'ADD TO CART';
        // Hide all Klaviyo forms when variant is available
        const klaviyoForms = document.querySelectorAll(
          '.js-klaviyo-stock-form'
        );
        klaviyoForms.forEach(form => {
          form.classList.add('klaviyo__stock-form--hidden');
          form.setAttribute('aria-hidden', 'true');
        });
      } else {
        addToCartButton.disabled = true;
        buttonText.innerHTML = 'SOLD OUT';

        // Hide all Klaviyo forms first
        const klaviyoForms = document.querySelectorAll(
          '.js-klaviyo-stock-form'
        );
        klaviyoForms.forEach(form => {
          form.classList.add('klaviyo__stock-form--hidden');
          form.setAttribute('aria-hidden', 'true');
        });

        // Show the appropriate Klaviyo form for this variant
        // First try variant-specific form
        const variantKlaviyoForm = document.querySelector(
          `.js-klaviyo-stock-form[data-variant-id="${variant.id}"]`
        );
        if (variantKlaviyoForm) {
          variantKlaviyoForm.classList.remove('klaviyo__stock-form--hidden');
          variantKlaviyoForm.setAttribute('aria-hidden', 'false');
        } else {
          // Fallback to product-level form (for products with only default variant)
          const productKlaviyoForm = document.querySelector(
            '.js-klaviyo-stock-form:not([data-variant-id])'
          );
          if (productKlaviyoForm) {
            productKlaviyoForm.classList.remove('klaviyo__stock-form--hidden');
            productKlaviyoForm.setAttribute('aria-hidden', 'false');
          }
        }
      }
    }

    // Update button active states
    this.updateButtonActiveStates(variant);

    // Handle out of stock forms
    this.handleOutOfStockForms(variant);

    // Update the previous variant reference
    this.previousVariant = variant;
  }

  /**
   * Updates active states for variant buttons
   * @param {Object} variant - The selected variant object
   */
  updateButtonActiveStates(variant) {
    // First make everything inactive
    const containers = document.getElementsByClassName('button-toggle');
    for (let i = 0; i < containers.length; i++) {
      containers[i].classList.remove('active');
      containers[i].firstElementChild.classList.remove('active');
    }

    // Now activate the relevant things
    const optionIndex = document.getElementById('buttonOptionIndex').value;
    const activeButton = document.querySelector(
      `[data-option-value="${variant[`option${optionIndex}`]}"]`
    );

    if (activeButton) {
      activeButton.classList.add('active');
      if (activeButton.firstElementChild) {
        activeButton.firstElementChild.classList.add('active');
      }
    }
  }

  /**
   * Handles display of out-of-stock forms
   * @param {Object} variant - The selected variant object
   */
  handleOutOfStockForms(variant) {
    const availableForm = document.getElementById(variant.title + '--oos-form');
    const oosForms = document.getElementsByClassName('oos-form');
    const addToCartContainer = document.getElementsByClassName(
      'product-add-to-cart-container'
    )[0];

    // Hide all OOS forms first
    for (let i = 0; i < oosForms.length; i++) {
      oosForms[i].style.display = 'none';
    }

    if (availableForm) {
      if (!variant.available) {
        availableForm.style.marginTop = '-45px';
        availableForm.style.display = 'block';
        if (addToCartContainer) {
          addToCartContainer.style.display = 'none';
        }
      } else if (addToCartContainer) {
        addToCartContainer.style.display = 'block';
      }
    } else if (addToCartContainer) {
      addToCartContainer.style.display = 'block';
    }
  }

  /**
   * Closes the refill confirmation modal
   * Reverts to previous selection if no confirmation was made
   */
  closeRefillConfirmModal() {
    // If user closes modal without selecting, ensure the previous radio stays selected
    if (this.pendingRefillConfirmation && this.previousSelectedRadio) {
      this.previousSelectedRadio.checked = true;
    }

    this.pendingRefillConfirmation = false;
    this.pendingRadioTarget = null;
    window.pendingRefillRadio = null;
  }

  /**
   * Handles the user's response to the refill confirmation modal
   * @param {boolean} hasShells - Whether the user has shells (Yes=true, No=false)
   */
  confirmRefillSelection(hasShells) {
    console.log('confirmRefillSelection called with hasShells:', hasShells);
    console.log('pendingRadioTarget:', this.pendingRadioTarget);
    console.log('window.pendingRefillRadio:', window.pendingRefillRadio);

    // Set processing flag to prevent event loop
    // This prevents the handleRadioChange from triggering another modal
    this.processingConfirmation = true;

    // Get the target radio button - use the global variable as backup
    const targetRadio = this.pendingRadioTarget || window.pendingRefillRadio;

    // Close the modal using BorouxModal
    this.refillModal.close();

    // Set pendingRefillConfirmation to false BEFORE dispatching events
    this.pendingRefillConfirmation = false;

    if (hasShells) {
      // User confirms they have shells, proceed with the refill variant
      if (targetRadio) {
        console.log('Setting checked=true on targetRadio:', targetRadio);

        // Check the radio button
        targetRadio.checked = true;

        // Update this as the previous selected radio
        this.previousSelectedRadio = targetRadio;

        // Dispatch a native change event to trigger the variant change
        console.log('Dispatching change event');
        const changeEvent = new Event('change', {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        targetRadio.dispatchEvent(changeEvent);

        // If the change event didn't work, try clicking the radio button
        setTimeout(() => {
          console.log('Trying click as fallback');
          targetRadio.click();
        }, 100);

        console.log('Change event dispatched');
      } else {
        console.error('No target radio button found!');
      }
    } else {
      // User doesn't have shells, keep the previous variant selected
      if (this.previousSelectedRadio) {
        console.log(
          'Setting previousSelectedRadio checked=true:',
          this.previousSelectedRadio
        );
        this.previousSelectedRadio.checked = true;
      } else {
        console.error('previousSelectedRadio is not set!');
      }
    }

    // Clear the pending radio targets
    this.pendingRadioTarget = null;
    window.pendingRefillRadio = null;

    // Reset processing flag after a short delay to ensure all events have completed
    setTimeout(() => {
      this.processingConfirmation = false;
    }, 500);
  }

  /**
   * Updates price displays based on variant
   * @param {Object} variant - The selected variant object
   */
  updatePriceDisplays(variant) {
    // Update price in Add to Cart button
    const atcButtonPrice = document.getElementById('atc_button_price');
    const atcButtonComparePrice = document.getElementById(
      'atc_button_compare_price'
    );

    if (atcButtonPrice) {
      // Format price for display
      const formattedPrice = this.formatPrice(variant.price);
      atcButtonPrice.innerHTML = formattedPrice;
    }

    if (atcButtonComparePrice) {
      if (variant.compare_at_price) {
        const formattedComparePrice = this.formatPrice(
          variant.compare_at_price
        );
        atcButtonComparePrice.innerHTML = formattedComparePrice;
        atcButtonComparePrice.style.display = 'block';
      } else {
        atcButtonComparePrice.style.display = 'none';
      }
    }

    // Update main product price display if it exists
    const mainPriceContainer = document.querySelector(
      '.product-price-container'
    );
    if (mainPriceContainer) {
      const mainPrice = mainPriceContainer.querySelector('.main-price');
      const comparePrice = mainPriceContainer.querySelector('.compare-price');

      if (mainPrice) {
        mainPrice.innerHTML = this.formatPrice(variant.price);
      }

      if (comparePrice) {
        if (variant.compare_at_price) {
          comparePrice.innerHTML = this.formatPrice(variant.compare_at_price);
          comparePrice.style.display = 'block';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    }
  }

  /**
   * Formats price for display
   * @param {number} price - The price in cents
   * @returns {string} - Formatted price string
   */
  formatPrice(price) {
    // Use existing formatPriceWithCode function if available
    if (typeof window.formatPriceWithCode === 'function') {
      return window.formatPriceWithCode(this.getRenderedPrice(price));
    }

    // Basic fallback formatting
    return '$' + (price / 100).toFixed(2);
  }

  /**
   * Gets rendered price value
   * @param {number} price - The price in cents
   * @returns {string|number} - Rendered price value
   */
  getRenderedPrice(price) {
    // Use existing getRenderedPrice function if available
    if (typeof window.getRenderedPrice === 'function') {
      return window.getRenderedPrice(price);
    }

    // Basic fallback formatting
    return (price / 100).toFixed(2);
  }
}

/**
 * Initialize the fluoride options modal
 */
function initFluorideFilterCountModal() {
  // Create a new modal instance using the existing DOM element
  const FluorideFilterCountModal = new BorouxModal({
    id: 'fluoride-filter-count-modal',
    onOpen: () => {
      console.log('Fluoride modal opened');
    },
    onClose: () => {
      console.log('Fluoride modal closed');
    },
  });

  // Add click event listeners to all buttons with the data-open-fluoride-modal attribute
  document
    .querySelectorAll('[data-open-fluoride-filter-count-modal]')
    .forEach(button => {
      button.addEventListener('click', () => {
        FluorideFilterCountModal.open();
      });
    });

  // Expose the modal instance to the global scope for potential use elsewhere
  window.fluorideFilterCountModal = FluorideFilterCountModal;
}

/**
 * Initialize the fluoride filter subscription guide modal
 */
function initFluorideFilterSubscriptionGuideModal() {
  // Create a new modal instance using the existing DOM element
  const FluorideFilterSubscriptionGuideModal = new BorouxModal({
    id: 'fluoride-filter-subscription-guide-modal',
    onOpen: () => {
      console.log('Fluoride subscription guide modal opened');
    },
    onClose: () => {
      console.log('Fluoride subscription guide modal closed');
    },
  });

  // Add click event listeners to all elements with the data-open-fluoride-filter-subscription-guide-modal attribute
  document
    .querySelectorAll('[data-open-fluoride-filter-subscription-guide-modal]')
    .forEach(button => {
      console.log('Found subscription guide button:', button);
      button.addEventListener('click', e => {
        e.preventDefault();
        console.log('Subscription guide button clicked');
        FluorideFilterSubscriptionGuideModal.open();
      });
    });

  // Expose the modal instance to the global scope for potential use elsewhere
  window.fluorideFilterSubscriptionGuideModal =
    FluorideFilterSubscriptionGuideModal;
}

/**
 * Fluoride Modal Handler
 *
 * Initializes and manages the fluoride options modal
 */
document.addEventListener('DOMContentLoaded', () => {
  initFluorideFilterCountModal();
  initFluorideFilterSubscriptionGuideModal();
});

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Create instance
  const fluorideOptions = new FluorideOptions();

  // Make instance methods available globally for HTML onclick attributes
  // These are needed because HTML onclick attributes can't directly access class instance methods
  window.closeRefillConfirmModal = function () {
    console.log('window.closeRefillConfirmModal called');
    fluorideOptions.closeRefillConfirmModal();
  };

  window.confirmRefillSelection = function (hasShells) {
    console.log(
      'window.confirmRefillSelection called with hasShells:',
      hasShells
    );
    fluorideOptions.confirmRefillSelection(hasShells);
  };
});
